import * as React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ProfileFormData } from '../types';
import { ProfilesService } from '../services/profiles-service';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, Save } from 'lucide-react';

export function ProfileFormPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  
  // Form state
  const [formData, setFormData] = React.useState<ProfileFormData>({
    profile_full_name: '',
    profile_title: '',
    profile_photo: '/profiles/placeholder.jpg',
    profile_summary: '',
    profile_bio: '',
    profile_position_type: 'executive_team',
    profile_order_weight: 999,
    profile_show_active: true
  });

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle select changes
  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle switch changes
  const handleSwitchChange = (name: string, checked: boolean) => {
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Validate form data
      if (!formData.profile_full_name || !formData.profile_title) {
        toast({
          title: 'Validation Error',
          description: 'Please fill in all required fields.',
          variant: 'destructive'
        });
        setIsSubmitting(false);
        return;
      }
      
      // Create profile
      await ProfilesService.createProfile(formData);
      
      toast({
        title: 'Profile Created',
        description: 'The profile has been created successfully.'
      });
      
      // Navigate back to profiles list
      navigate({ to: '/profiles' });
    } catch (error) {
      console.error('Error creating profile:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create profile',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <PageHeader
          title="Add Profile"
          description="Create a new profile for the executive team or board of directors."
        />
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate({ to: '/profiles' })}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={isSubmitting} onClick={handleSubmit}>
            <Save className="mr-2 h-4 w-4" />
            {isSubmitting ? 'Saving...' : 'Save Profile'}
          </Button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="profile_full_name">Full Name *</Label>
                <Input
                  id="profile_full_name"
                  name="profile_full_name"
                  value={formData.profile_full_name}
                  onChange={handleInputChange}
                  placeholder="John Doe"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="profile_title">Title *</Label>
                <Input
                  id="profile_title"
                  name="profile_title"
                  value={formData.profile_title}
                  onChange={handleInputChange}
                  placeholder="Chief Executive Officer"
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="profile_position_type">Position Type *</Label>
                <Select
                  value={formData.profile_position_type}
                  onValueChange={(value) => handleSelectChange('profile_position_type', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select position type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="executive_team">Executive Team</SelectItem>
                    <SelectItem value="board_member">Board Member</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="profile_order_weight">Display Order</Label>
                <Input
                  id="profile_order_weight"
                  name="profile_order_weight"
                  type="number"
                  value={formData.profile_order_weight.toString()}
                  onChange={handleInputChange}
                  placeholder="999"
                />
                <p className="text-xs text-muted-foreground">Lower numbers appear first</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <Switch
                id="profile_show_active"
                checked={formData.profile_show_active}
                onCheckedChange={(checked) => handleSwitchChange('profile_show_active', checked)}
              />
              <Label htmlFor="profile_show_active">Active</Label>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Profile Photo</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="profile_photo">Photo URL</Label>
              <Input
                id="profile_photo"
                name="profile_photo"
                value={formData.profile_photo}
                onChange={handleInputChange}
                placeholder="/profiles/john_doe.jpg"
              />
              <p className="text-xs text-muted-foreground">
                Enter the path to the profile photo. For example: /profiles/john_doe.jpg
              </p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Biography</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="profile_summary">Summary</Label>
              <Textarea
                id="profile_summary"
                name="profile_summary"
                value={formData.profile_summary || ''}
                onChange={handleInputChange}
                placeholder="Brief summary of the person's role and experience"
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="profile_bio">Full Biography</Label>
              <Textarea
                id="profile_bio"
                name="profile_bio"
                value={formData.profile_bio || ''}
                onChange={handleInputChange}
                placeholder="Detailed biography with professional experience and background"
                rows={6}
              />
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}

export default ProfileFormPage;
